/**
 * Merge history (TRUS-558 merge v2): every duplicate merge in this organization,
 * newest first, with a one-shot Revert.
 *
 * A revert restores everything the merge changed, in reverse — except values staff
 * edited after the merge, which are kept and listed here as conflicts. Records
 * created after the merge stay with the kept record. The duplicates are reactivated
 * and the group reappears in duplicate review.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, History, Loader2, RefreshCw, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { membersAPI, type DuplicateDismissal, type MergeHistoryEntry } from '@/services/api/members';
import { countChangedRows, revertConflictRows } from './mergeFlow';

interface MergeHistoryProps {
  /** Called after a revert so the caller can refresh duplicate groups / member lists. */
  onReverted?: () => void;
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

const MergeHistory: React.FC<MergeHistoryProps> = ({ onReverted }) => {
  const { t } = useTranslation();
  const [merges, setMerges] = useState<MergeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<MergeHistoryEntry | null>(null);
  const [reverting, setReverting] = useState(false);
  const [dismissals, setDismissals] = useState<DuplicateDismissal[]>([]);
  const [undoingId, setUndoingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, dis] = await Promise.all([
        membersAPI.listDuplicateMerges(),
        membersAPI.listDuplicateDismissals(),
      ]);
      setMerges(res.merges || []);
      setDismissals(dis.dismissals || []);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t('members.duplicates.history.loadFailed', 'Could not load merge history.')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = (m: MergeHistoryEntry, id: number) => {
    const rec = m.members[String(id)];
    const name = rec ? [rec.first_name, rec.last_name].filter(Boolean).join(' ') : '';
    return `${name || t('members.duplicates.memberFallback', 'Member')} #${id}`;
  };

  const handleUndoDismissal = async (d: DuplicateDismissal) => {
    setUndoingId(d.id);
    setError(null);
    try {
      await membersAPI.undoDuplicateDismissal(d.id);
      setDismissals((list) => list.filter((x) => x.id !== d.id));
      onReverted?.();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t('members.duplicates.history.undoDismissFailed', 'Could not undo this dismissal.')
      );
    } finally {
      setUndoingId(null);
    }
  };

  const dismissedName = (d: DuplicateDismissal, id: number) => {
    const rec = d.members[String(id)];
    const name = rec ? [rec.first_name, rec.last_name].filter(Boolean).join(' ') : '';
    return `${name || t('members.duplicates.memberFallback', 'Member')} #${id}`;
  };

  const handleRevert = async () => {
    if (!confirming) return;
    setReverting(true);
    setError(null);
    try {
      const res = await membersAPI.revertDuplicateMerge(confirming.id);
      setMerges((list) =>
        list.map((m) =>
          m.id === confirming.id
            ? { ...m, reverted_at: new Date().toISOString(), revert_report: res.revert_report }
            : m
        )
      );
      setConfirming(null);
      onReverted?.();
      void load();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ||
          t('members.duplicates.history.revertFailed', 'Could not revert this merge.')
      );
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t(
            'members.duplicates.history.intro',
            'Every merge in this organization, newest first. A merge can be reverted once.'
          )}
        </p>
        <Button
          variant="secondary"
          onClick={() => void load()}
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          {t('members.duplicates.history.refresh', 'Refresh')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('members.duplicates.history.loading', 'Loading merge history…')}
        </div>
      )}

      {!loading && merges.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            <History className="w-6 h-6 mx-auto mb-2" />
            {t('members.duplicates.history.empty', 'No merges yet.')}
          </CardContent>
        </Card>
      )}

      {merges.map((m) => {
        const conflicts = revertConflictRows(m.revert_report);
        const rows = countChangedRows(m.summary?.counts?.changed);
        const cases = m.summary?.cases ?? [];
        return (
          <Card key={m.id}>
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">
                    {t('members.duplicates.history.mergedInto', {
                      dups: m.duplicate_member_ids.map((id) => nameOf(m, id)).join(', '),
                      survivor: nameOf(m, m.survivor_member_id),
                      defaultValue: '{{dups}} merged into {{survivor}}',
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('members.duplicates.history.byWhen', {
                      who: m.merged_by_name || m.merged_by || '—',
                      when: formatWhen(m.merged_at),
                      defaultValue: 'by {{who}} on {{when}}',
                    })}
                  </div>
                </div>
                {m.reverted_at ? (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Check className="w-3.5 h-3.5" />
                    {t('members.duplicates.history.revertedOn', {
                      when: formatWhen(m.reverted_at),
                      who: m.reverted_by_name || m.reverted_by || '—',
                      defaultValue: 'Reverted {{when}} by {{who}}',
                    })}
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setConfirming(m)}
                  >
                    <Undo2 className="w-4 h-4 mr-1" />
                    {t('members.duplicates.history.revert', 'Revert')}
                  </Button>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                {t('members.duplicates.history.counts', {
                  rows,
                  fields: m.summary?.fields?.length ?? 0,
                  cases: cases.length,
                  defaultValue: '{{rows}} records changed · {{fields}} field(s) filled or chosen · {{cases}} case(s)',
                })}
              </div>
              {cases.length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc pl-5">
                  {cases.map((c) => (
                    <li key={c.enrollment_id}>
                      {t('members.duplicates.history.caseAction', {
                        program: c.program_name,
                        id: c.enrollment_id,
                        action: t(`members.duplicates.history.action.${c.action ?? 'none'}`, c.action ?? '—'),
                        defaultValue: '{{program}} (case {{id}}): {{action}}',
                      })}
                    </li>
                  ))}
                </ul>
              )}

              {m.revert_report && (
                <div className="rounded border border-dashed p-2 text-xs space-y-1">
                  <div className="font-medium">
                    {t('members.duplicates.history.reportTitle', 'Revert report')}
                  </div>
                  <div className="text-muted-foreground">
                    {t('members.duplicates.history.reportRestored', {
                      count: Object.values(m.revert_report.restored || {}).reduce((a, b) => a + b, 0),
                      defaultValue: '{{count}} record(s) restored',
                    })}
                  </div>
                  {conflicts.length === 0 ? (
                    <div className="text-muted-foreground">
                      {t('members.duplicates.history.noConflicts', 'Nothing had changed since the merge — fully restored.')}
                    </div>
                  ) : (
                    <>
                      <div className="text-amber-700 dark:text-amber-300">
                        {t('members.duplicates.history.conflictsIntro', {
                          count: conflicts.length,
                          defaultValue:
                            '{{count}} value(s) were edited after the merge and were kept as they are:',
                        })}
                      </div>
                      <ul className="list-disc pl-5">
                        {conflicts.map((c) => (
                          <li key={`${c.table}-${c.pk}-${c.column}`}>
                            {t('members.duplicates.history.conflictLine', {
                              what: c.field || `${c.table}.${c.column}`,
                              pk: c.pk,
                              wrote: show(c.mergeWrote),
                              current: show(c.current),
                              defaultValue: '{{what}} (#{{pk}}): merge wrote “{{wrote}}”, now “{{current}}”',
                            })}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {!loading && dismissals.length > 0 && (
        <div className="space-y-2 pt-2">
          <h3 className="text-sm font-medium">
            {t('members.duplicates.history.dismissedTitle', 'Dismissed as different people')}
          </h3>
          {dismissals.map((d) => (
            <Card key={d.id}>
              <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm">{d.member_ids.map((id) => dismissedName(d, id)).join(', ')}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('members.duplicates.history.byWhen', {
                      who: d.resolved_by_name || d.resolved_by || '—',
                      when: formatWhen(d.resolved_at),
                      defaultValue: 'by {{who}} on {{when}}',
                    })}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => void handleUndoDismissal(d)}
                  disabled={undoingId === d.id}
                >
                  {undoingId === d.id ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Undo2 className="w-4 h-4 mr-1" />
                  )}
                  {t('members.duplicates.history.undoDismiss', 'Undo')}
                </Button>
              </CardContent>
            </Card>
          ))}
          <p className="text-xs text-muted-foreground">
            {t(
              'members.duplicates.history.undoDismissHelp',
              'Undo puts the group back in duplicate review. The profiles themselves were never changed by the dismissal.'
            )}
          </p>
        </div>
      )}

      <Dialog open={!!confirming} onOpenChange={(open) => !open && !reverting && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('members.duplicates.history.confirmTitle', 'Revert this merge?')}</DialogTitle>
            <DialogDescription>
              {t(
                'members.duplicates.history.confirmBody',
                'The duplicate records are reactivated and everything the merge moved or changed goes back: field values, cases, notes, documents and other records. Anything edited since the merge is kept as it is and listed in the revert report. Records created after the merge stay with the kept record. The group will show up again in duplicate review. A merge can be reverted only once.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(null)} disabled={reverting}>
              {t('members.duplicates.history.confirmCancel', 'Cancel')}
            </Button>
            <Button
              variant="default"
              onClick={() => void handleRevert()}
              disabled={reverting}
            >
              {reverting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Undo2 className="w-4 h-4 mr-1" />
              )}
              {t('members.duplicates.history.confirmRevert', 'Revert merge')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MergeHistory;
